'use client';
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Keyboard, Pagination, Navigation, Autoplay } from 'swiper/modules';
import { redirect, RedirectType, useRouter } from 'next/navigation';

import ImageWithFallback from '@/components/common/ImageWithFallback';
import { ShelfItem } from '@/context/global/types/homeShelf';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { useAuthStore } from '@/context/global/stores/authStore';
import CWModalAdvert from '@/context/global/components/g00/g00-d00-g017-cw-modal-advert';
import { useState } from 'react';

// Swiper chevron color override
const swiperCustomStyle = `
  .swiper-button-next, .swiper-button-prev {
    color: #fff !important;
    --swiper-navigation-color: #fff !important;
    filter: drop-shadow(0 2px 8px rgba(0,0,0,0.25));
  }
  .swiper-pagination {
    bottom: 24px !important;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    z-index: 20;
  }
  .swiper-pagination-bullet {
    width: 12px;
    height: 12px;
    border-radius: 9999px;
    background: rgba(255,255,255,0.5);
    opacity: 1;
    box-shadow: 0 2px 8px 0 rgba(0,0,0,0.10);
    margin: 0 !important;
  }
  .swiper-pagination-bullet-active {
    width: 32px;
    height: 12px;
    background: var(--color-ais-green);
    box-shadow: 0 4px 16px 0 rgba(178,210,52,0.25);
  }
`;

export default function CWHeroBannerCarousel({ items = [] }: { items?: ShelfItem[] }) {
  const { setSelectedSong } = useKaraokeStore();
  const { user } = useAuthStore();
  const [isAdvertModalOpen, setIsAdvertModalOpen] = useState(false);

  const handleBannerClick = (
    action: string | undefined,
    contentUrl: string | undefined,
  ) => {
    if (!action || !contentUrl) return;

    switch (action) {
      case 'web':
        window.open(contentUrl, '_blank');
        break;
      case 'event':
      case 'category':
      case 'playlist':
        redirect(contentUrl, RedirectType.push);
        break;
      case 'song':
        // Handled by "ร้องเลย" button separately
        break;
    }
  };

  return (
    <>
      <style>{swiperCustomStyle}</style>
      <section className="relative mx-auto mt-0 flex aspect-[4/3] w-full max-w-screen-2xl select-none items-end justify-center overflow-hidden md:aspect-[16/6] lg:aspect-[16/5]">
        <Swiper
          slidesPerView={1}
          spaceBetween={30}
          loop={true}
          pagination={{ clickable: true }}
          navigation={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          modules={[Keyboard, Pagination, Navigation, Autoplay]}
          className="h-full w-full"
          style={{ position: 'absolute', inset: 0 }}
        >
          {items.map((banner, idx) => {
            const action = banner.nextTo?.action;
            const key = banner.id ? `${banner.id}-${idx}` : idx;
            return (
              <SwiperSlide key={key}>
                <div className="relative mx-auto aspect-[16/9] h-full w-full max-w-screen-2xl md:aspect-[16/6] lg:aspect-[16/5]">
                  <ImageWithFallback
                    src={banner.coverUrl}
                    alt={banner.title || `Banner ${idx + 1}`}
                    className="absolute inset-0 h-full w-full rounded-none bg-cover bg-center bg-no-repeat object-cover"
                  />
                  {/* Top gradient overlay for navbar */}
                  <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-32 bg-gradient-to-b from-black/60 via-black/30 to-transparent md:h-40" />
                  {/* Bottom gradient overlay */}
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-1/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  {/* Song type: Play Now button overlay */}
                  {action === 'song' && (
                    <button
                      className="absolute bottom-14 left-1/2 z-20 flex w-max -translate-x-1/2 items-center justify-center gap-2 rounded-[10px] bg-white py-2 pl-2 pr-3 shadow-lg transition hover:bg-gray-100 active:scale-95 sm:pl-3 sm:pr-2 md:bottom-[50.62px] md:left-12 md:translate-x-0 md:py-2 md:pl-8 md:pr-8 lg:left-20 lg:pl-10 lg:pr-10"
                      style={{ minWidth: 120, maxWidth: '100%' }}
                      onClick={() => {
                        if (banner.conditions == 2 && user?.badge == 3) {
                          setIsAdvertModalOpen(true);
                          return;
                        }
                        setSelectedSong({ ...banner, id: Number(banner.nextTo?.contentRefined) });
                      }}
                    >
                      <img
                        src={'/assets/ic-play.svg'}
                        alt="play"
                        className="h-5 w-5 md:h-6 md:w-6"
                      />
                      <span className="cursor-pointer whitespace-nowrap text-[16px] font-bold leading-6 text-[#030606] md:text-[24px] md:leading-8">
                        ร้องเลย
                      </span>
                    </button>
                  )}
                  {/* Clickable banner overlay for non-song actions */}
                  {action && action !== 'song' && banner.contentUrl && (
                    <div
                      onClick={() => handleBannerClick(action, banner.contentUrl)}
                      className="absolute inset-0 z-20 cursor-pointer"
                      role="button"
                      aria-label={banner.title || 'Banner link'}
                    />
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>
      {isAdvertModalOpen && (
        <CWModalAdvert
          isOpen={isAdvertModalOpen}
          onClose={() => setIsAdvertModalOpen(false)}
        />
      )}
    </>
  );
}
